<template>
  <div class="min-h-screen flex items-center justify-center bg-white px-4">
    <div class="w-full max-w-md flex flex-col items-center">
      <img src="/static/logo.png" alt="Logo IBOLINK" class="w-28 h-28 mb-8 drop-shadow-lg" />
      <form @submit.prevent="handleLogin" class="w-full flex flex-col gap-7">
        <h2 class="text-2xl font-bold text-center text-gray-800 mb-2 tracking-wide">Iniciar sesión</h2>
        <div class="relative">
          <input
            v-model="email"
            type="email"
            placeholder="Correo electrónico"
            class="peer border-0 border-b-2 border-gray-200 focus:border-[#00B8F4] bg-transparent px-2 py-3 w-full text-gray-800 focus:outline-none transition placeholder-gray-400"
            :disabled="loading"
            required
          />
          <span class="absolute left-0 bottom-0 w-0 h-0.5 bg-[#00B8F4] transition-all peer-focus:w-full"></span>
        </div>
        <div class="relative">
          <input
            v-model="password"
            type="password"
            placeholder="Contraseña"
            class="peer border-0 border-b-2 border-gray-200 focus:border-[#00B8F4] bg-transparent px-2 py-3 w-full text-gray-800 focus:outline-none transition placeholder-gray-400"
            :disabled="loading"
            required
          />
          <span class="absolute left-0 bottom-0 w-0 h-0.5 bg-[#00B8F4] transition-all peer-focus:w-full"></span>
        </div>
        <select
          v-model="userType"
          class="border-0 border-b-2 border-gray-200 focus:border-[#00B8F4] bg-transparent px-2 py-3 w-full text-gray-800 focus:outline-none transition"
          :disabled="loading"
          required
        >
          <option disabled value="">Tipo de usuario</option>
          <option value="CLT_IBL">Administrador</option>
          <option value="OPE_IBL">Operador</option>
          <option value="CST_IBL">Consulta</option>
        </select>
        <button
          type="submit"
          :disabled="loading || !isFormValid"
          class="group relative w-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:from-blue-600 hover:via-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-2xl disabled:shadow-md transition-all duration-300 transform hover:scale-[1.02] disabled:transform-none disabled:cursor-not-allowed flex items-center justify-center mt-4 overflow-hidden"
        >
          <!-- Shimmer effect -->
          <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          
          <!-- Content -->
          <div class="relative flex items-center justify-center space-x-3">
            <!-- Icon -->
            <svg v-if="!loading" class="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
            </svg>
            
            <!-- Text -->
            <span v-if="!loading" class="text-base font-medium tracking-wide transition-all group-hover:tracking-wider">Iniciar Sesión</span>
            
            <!-- Loading spinner -->
            <div v-else class="flex items-center space-x-2">
              <div class="loader"></div>
              <span class="text-base font-medium">Conectando...</span>
            </div>
          </div>
        </button>
        <transition name="fade">
          <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {{ error }}
          </div>
        </transition>
        <div v-if="loading" class="text-center text-gray-500 text-sm">
          Iniciando sesión...
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'Login',
  data() {
    return {
      email: '',
      password: '',
      userType: ''
    }
  },
  computed: {
    ...mapGetters('auth', ['loading', 'error']),
    isFormValid() {
      return this.email && this.password && this.userType
    }
  },
  methods: {
    ...mapActions('auth', ['login']),
    
    async handleLogin() {
      try {
        const credentials = {
          email: this.email,
          password: this.password,
          userType: this.userType
        }
        
        await this.login(credentials)
        
        this.$router.push('/dashboard')
        
      } catch (error) {
        // Login error - handled silently
      }
    }
  }
}
</script>

<style>
.loader {
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #ffffff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Enhanced button effects */
button[type="submit"] {
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

/* Shimmer effect */
button[type="submit"]::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s;
}

button[type="submit"]:hover::before {
  left: 100%;
}

/* Ripple effect */
button[type="submit"]:active::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  background: rgba(255, 255, 255, 0.5);
  opacity: 1;
  border-radius: 100%;
  transform: scale(1, 1) translate(-50%);
  transform-origin: 50% 50%;
  animation: ripple 0.6s ease-out;
}

@keyframes ripple {
  0% {
    transform: scale(0, 0);
    opacity: 0.5;
  }
  20% {
    transform: scale(25, 25);
    opacity: 0.3;
  }
  100% {
    opacity: 0;
    transform: scale(40, 40);
  }
}

/* Focus ring for accessibility */
button[type="submit"]:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

/* Disabled state animation */
button[type="submit"]:disabled {
  animation: disabled-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes disabled-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Enhanced shadow on hover */
button[type="submit"]:hover:not(:disabled) {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Active state */
button[type="submit"]:active:not(:disabled) {
  transform: scale(0.98);
}

/* Smooth transitions for all interactive elements */
button, input, select {
  transition: all 0.2s ease-in-out;
}

/* Icon rotation on hover */
button[type="submit"]:hover svg {
  animation: icon-bounce 0.6s ease-in-out;
}

@keyframes icon-bounce {
  0%, 100% {
    transform: translateX(0) rotate(0deg);
  }
  50% {
    transform: translateX(3px) rotate(5deg);
  }
}

/* Text animation on hover */
button[type="submit"]:hover .group-hover\:tracking-wider {
  letter-spacing: 0.1em;
}

/* Loading state enhancements */
button[type="submit"]:disabled .loader {
  border-top-color: #ffffff;
  border-right-color: rgba(255, 255, 255, 0.3);
  border-bottom-color: rgba(255, 255, 255, 0.3);
  border-left-color: rgba(255, 255, 255, 0.3);
}

/* Button glow effect */
button[type="submit"]:hover:not(:disabled) {
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 20px rgba(59, 130, 246, 0.3);
}

/* Success state (for future use) */
button[type="submit"].success {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
}

/* Error state (for future use) */
button[type="submit"].error {
  background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
}
</style> 


// WEBPACK FOOTER //
// src/views/Login.vue